import { useEffect } from "react";

export const SITE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const DEFAULT_IMAGE = `${SITE_URL}/api/share/default.png?v=2`;

interface Seo {
  title: string;
  description: string;
  path?: string;
  image?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
  ogTitle?: string;
  keywords?: string;
  ogDescription?: string;
}

function setMeta(attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

export function useSeo({ title, description, path, image, noindex, jsonLd, ogTitle, ogDescription, keywords }: Seo) {
  const ld = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    const url = `${SITE_URL}${path ?? window.location.pathname}`;
    const ogT = ogTitle || title, ogD = ogDescription || description;
    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "keywords", keywords || "");
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setMeta("property", "og:title", ogT);
    setMeta("property", "og:description", ogD);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image || DEFAULT_IMAGE);
    setMeta("name", "twitter:title", ogT);
    setMeta("name", "twitter:description", ogD);
    setMeta("name", "twitter:image", image || DEFAULT_IMAGE);
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = url;
    let script: HTMLScriptElement | null = null;
    if (ld) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seo = "page";
      script.text = ld;
      document.head.appendChild(script);
    }
    return () => script?.remove();
  }, [title, description, path, image, noindex, ld, ogTitle, ogDescription, keywords]);
}

export const faqJsonLd = (faqs: { q: string; a: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});
